import { useEffect, useRef } from 'react';

interface PlasmaSmokeOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function PlasmaSmokeOverlay({ colors, opacity = 0.3 }: PlasmaSmokeOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    const animate = () => {
      const imageData = ctx.createImageData(canvas.width, canvas.height);
      const data = imageData.data;
      time += 0.05;

      for (let y = 0; y < canvas.height; y += 2) {
        for (let x = 0; x < canvas.width; x += 2) {
          const index = (y * canvas.width + x) * 4;
          
          // Plasma calculation (same as original)
          const value = Math.sin(x * 0.01 + time) +
                       Math.sin(y * 0.01 + time) +
                       Math.sin((x + y) * 0.01 + time) +
                       Math.sin(Math.sqrt(x * x + y * y) * 0.01 + time);
          
          const normalized = (value + 4) / 8;
          
          // Map to dark grey smoke colors instead of SUI blue
          const baseGrey = Math.floor(30 + normalized * 60);  // Range: 30-90 (dark grey)
          const r = baseGrey;
          const g = baseGrey;
          const b = baseGrey + Math.floor(normalized * 20);   // Slight blue tint for depth
          const a = Math.floor(normalized * 120 + 40);        // Slightly more opacity for smoke effect
          
          data[index] = r;
          data[index + 1] = g;
          data[index + 2] = b;
          data[index + 3] = a;
          
          // Fill 2x2 block for performance
          if (x + 1 < canvas.width) {
            const nextIndex = (y * canvas.width + (x + 1)) * 4;
            data[nextIndex] = r;
            data[nextIndex + 1] = g;
            data[nextIndex + 2] = b;
            data[nextIndex + 3] = a;
          }
          
          if (y + 1 < canvas.height) {
            const belowIndex = ((y + 1) * canvas.width + x) * 4;
            data[belowIndex] = r;
            data[belowIndex + 1] = g;
            data[belowIndex + 2] = b;
            data[belowIndex + 3] = a;
            
            if (x + 1 < canvas.width) {
              const diagIndex = ((y + 1) * canvas.width + (x + 1)) * 4;
              data[diagIndex] = r;
              data[diagIndex + 1] = g;
              data[diagIndex + 2] = b;
              data[diagIndex + 3] = a;
            }
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
